import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { styled } from '@mui/material/styles'
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import UploadIcon from '@mui/icons-material/Upload'

import { StlViewer } from 'react-stl-viewer'
import { ResponsiveContainer, Line, LineChart, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'

import { useAutoSave, AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import toFixed from '../lib/toFixed'
import s3 from '../lib/s3.js'
import { getStaticStability } from '../lib/mr-bullet.js'
import { G7 } from '../lib/drag-curves.ts';
import { interpolate } from '../lib/trajectory.ts'
import getBoxes from '../lib/cfd-bounding-boxes.js'

let M_G7 = G7.map((v) => v[0]);
let D_G7 = G7.map((v) => v[1]);

const getG7Cd = mach => {
  return interpolate(mach, M_G7, D_G7)
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const aoas = ['0', '2']
const machs = [ '1.2', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0' ]
const calibers = [ '.177', '.204', '.224', '.243', '.257', '.264', '.277', '.284', '.308', '.338', '.357', '.375', '.408', '.416', '.458', '.510' ]

const Cfd = () => {
  const { save } = useAutoSave()
  const [ , setName ] = useAutoSaveState('name', '')
  const [ boxes, setBoxes ] = useAutoSaveState('boxes', [])
  const [ queued, setQueued ] = useAutoSaveState('queued', false)
  const [ mach, setMach ] = useAutoSaveState('mach', [ '2.5' ])
  const [ u, setU ] = useAutoSaveState('u', [ '865' ])
  const [ loading, setLoading ] = useState(false)
  const [ stlUrl, setStlUrl ] = useAutoSaveState('stlUrl', '')
  const [ diameter, setDiameter ] = useAutoSaveState('diameter', 0)
  const [ weight, setWeight ] = useAutoSaveState('weight', 0)
  const [ velocity, setVelocity ] = useAutoSaveState('velocity', 2800)
  const [ twist, setTwist ] = useAutoSaveState('twist', 8)
  const [ ix, setIx ] = useAutoSaveState('ix', 0)
  const [ iy, setIy ] = useAutoSaveState('iy', 0)
  const [ altitude, setAltitude ] = useAutoSaveState('altitude', 0)
  const [ , setPreview ] = useAutoSaveState('preview', null)
  const [ coeffs, setCoeffs ] = useState(null)
  const [ coeffResponses, setCoeffResponses ] = useState(null)
  const [ chartData, setChartData ] = useState([])
  const [ dragCoeffChartData, setDragCoeffChartData ] = useState([])
  const [ aeroCoeffChartData, setAeroCoeffChartData ] = useState([])
  const [ resTab, setResTab ] = useState(0)
  const [ renderTab, setRenderTab ] = useState(0)
  const [ coeffTab, setCoeffTab ] = useState(0)
  const [ renderings, setRenderings ] = useState({})

  const queueCfd = () => {
    setQueued(true)
    save()
  }

  useEffect(() => {
    if (coeffs) {
      setPreview(`Cd: ${toFixed(coeffs.cd, 3)}, Cmα: ${toFixed(coeffs.cma, 3)}, FF: ${toFixed(coeffs.ff, 3)}, BC: ${toFixed(coeffs.bc, 3)}`)
    }
  }, [coeffs, setPreview])

  useEffect(() => {
    if (!stlUrl) return
    const fetchData = async () => {
      try {
        const responses = {}
        const renderings = {}
        for (const i in u) {
          for (let a of aoas) {
            const v = u[i]
            const m = mach[i]
            if (a === '0.0') a = '0'
            if (a === '1.0') a = '2'
            if (a === '2.0') a = '2'
            if (a === '3.0') a = '3'
            if (a === '4.0') a = '4'
            if (a === '5.0') a = '5'
            const coeffsUrl = stlUrl.replace('bullet.stl', `${a}deg-${v}U-coeffs.dat`)
            try {
              const response = await axios.get(coeffsUrl)
              responses[`${a}deg-${v}U`] = response
              renderings[`Mach ${m} ${a}deg`] = stlUrl.replace('bullet.stl', `${a}deg-${v}U-render.png`)
            } catch (err) {
              console.log('could not get response', coeffsUrl)
            }
          }
        }
        console.log(responses)
        console.log({ renderings })
        setCoeffResponses(responses)
        setRenderings(renderings)
      } catch (err) {
        console.error(err)
      }
    }
    fetchData()
  }, [stlUrl, u])

  useEffect(() => {
    const newU = mach.map(m => getU(m, 77))
    if (newU.join() === u.join()) return
    setU(newU)
  }, [mach, setU])

  useEffect(() => {
    try {
      const coeffs = {}
      const charts = {}
      const dragCoeffChartData = []
      const aeroCoeffChartData = []
      for (const i in u) {
        for (const a of aoas) {
          const v = u[i]
          const m = mach[i]
          if (a == 0) continue
          if (!coeffResponses) continue
          const coeffResponse0 = coeffResponses[`0deg-${v}U`]
          const coeffResponse2 = coeffResponses[`${Math.round(a)}deg-${v}U`]
          if (!coeffResponse0 || !coeffResponse2) continue
          const times = coeffResponse0.data.split('\n').filter(line => line[0] !== '#').map(line => line.split(/\s+/)[0]).map(parseFloat).filter(v => !isNaN(v))
          const cds = coeffResponse0.data.split('\n').filter(line => line[0] !== '#').map(line => line.split(/\s+/)[2]).map(parseFloat).filter(v => !isNaN(v))
          const cdas = coeffResponse2.data.split('\n').filter(line => line[0] !== '#').map(line => line.split(/\s+/)[2]).map(parseFloat).filter(v => !isNaN(v))
          const cmas = coeffResponse2.data.split('\n').filter(line => line[0] !== '#').map(line => line.split(/\s+/)[1]).map(parseFloat).filter(v => !isNaN(v))
          const clas = coeffResponse2.data.split('\n').filter(line => line[0] !== '#').map(line => line.split(/\s+/)[3]).map(parseFloat).filter(v => !isNaN(v))
          console.log({ u, a, m, cds, cdas, cmas, clas })
          const sintheta = Math.sin(parseFloat(a) * (Math.PI / 180))
          const cd = cds.slice(-10).reduce((a, b) => a + b, 0) / 10
          const cda2 = (cdas.slice(-10).reduce((a, b) => a + b, 0) / 10 - cd) / (sintheta ** 2)
          const cla = (clas.slice(-10).reduce((a, b) => a + b, 0) / 10) / sintheta
          const cma = (cmas.slice(-10).reduce((a, b) => a + b, 0) / 10) / sintheta
          const g7Cd = getG7Cd(m)
          const ff = cd / g7Cd
          const bc = (weight / 7000) / (ff * diameter * diameter)
          const sg = getStaticStability(
            parseFloat(diameter),
            parseFloat(twist),
            parseFloat(cma),
            parseFloat(iy),
            parseFloat(ix),
            parseFloat(velocity),
            parseFloat(altitude)
          )
          console.log({ cd, cda2, cma, ff, bc, sg })
          coeffs[`Mach ${m}`] = { cd, cda2, cma, ff, bc, sg, cla, g7Cd, a, u: v, m }
          charts[`Mach ${m} Convergence`] = times.map((time, i) => ({ times: time, cd0: cds[i], cma2: cmas[i], cd2: cdas[i], cla2: clas[i]}))
          dragCoeffChartData.push({ a, cd, cda2, cma, cla, ff, bc, sg, u: v, m, g7Cd })
          aeroCoeffChartData.push({ a, cd, cda2, cma, cla, ff, bc, sg, u: v, m, g7Cd })
        }
      }
      const values = Object.values(coeffs)
      const avgBC = values.map(v => v.bc).reduce((a, b) => a + b, 0) / values.length
      for (const key in coeffs) {
        coeffs[key].avgBC = avgBC
      }
      dragCoeffChartData.sort((a, b) => a.u - b.u)
      aeroCoeffChartData.sort((a, b) => a.u - b.u)
      console.log({ coeffs, charts })
      setCoeffs(coeffs)
      setChartData(charts)
      setDragCoeffChartData(dragCoeffChartData)
      setAeroCoeffChartData(aeroCoeffChartData)
    } catch (err) {
      console.log(err)
    }
  }, [diameter, weight, velocity, twist, ix, iy, altitude, coeffResponses])

  const handleFileChange = event => {
    setLoading(true)
    const file = event.target.files[0]
    const reader = new FileReader()

    reader.onload = async () => {
      const id = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
      const uploadParams = {
        Bucket: 'bd-cfd',
        Key: `${id}/bullet.stl`,
        Body: file,
        ContentType: file.type
      }

      try {
        const data = await s3.upload(uploadParams).promise();
        console.log('Upload successful:', data.Location);
        setStlUrl(data.Location)
        setName(`CFD ${file.name}`)
        setLoading(false)
        const bytes = await file.bytes()
        const string = new TextDecoder().decode(bytes)
        const boxes = getBoxes(string)
        setBoxes(boxes)
        setTimeout(save, 1000)
        console.log(boxes)
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    reader.readAsDataURL(file)
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            CFD
          </Typography>
          {stlUrl && <SaveButton />}
        </Grid>
        <NameField />
        {loading && <Grid item xs={12}>
          <Paper sx={{ p: 5 }}>
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          </Paper>
        </Grid>}
        {!stlUrl && !loading && <Grid item xs={12}>
          <Paper sx={{ p: 5 }}>
            <Button fullWidth component="label" variant="contained" startIcon={<UploadIcon />}>
              Upload file
              <VisuallyHiddenInput type="file" onChange={handleFileChange} />
            </Button>
            <Alert sx={{ marginTop: 2 }} severity="info">
              Formatting requirements:
              <ul>
                <li>STL files must be ascii format.</li>
                <li>The CG of the bullet must be at the origin of the stl file.</li>
                <li>Units must be mm.</li>
              </ul>
            </Alert>
          </Paper>
        </Grid>}
        {stlUrl && <Grid item xs={6}>
          <Paper sx={{ p: 2, height: 485 }}>
            <Grid container xs={12} spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h4" component="h4" gutterBottom>
                    Bullet Details
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Diameter</InputLabel>
                  <Select
                    value={diameter}
                    label="Diameter"
                    onChange={e => setDiameter(e.target.value)}
                  >
                    {calibers.map((caliber) => (
                      <MenuItem key={caliber} value={caliber}>{caliber}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Bullet Weight" value={weight} onChange={e => setWeight(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">grains</InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Bullet Velocity" value={velocity} onChange={e => setVelocity(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">fps</InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Twist Rate" value={twist} onChange={e => setTwist(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in/rev</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Ix" value={ix} onChange={e => setIx(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm<sup>2</sup></InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Iy" value={iy} onChange={e => setIy(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm<sup>2</sup></InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Altitude" value={altitude} onChange={e => setAltitude(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">feet</InputAdornment> }} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>}
        {stlUrl && <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 485 }}>
            <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>3D Model</Typography>
            {stlUrl && <>
              <StlViewer
                style={{ height: 350, width: '100%', border: '1px solid #ccc' }}
                orbitControls
                url={stlUrl}
                shadows
                modelProps={{
                  color: '#B87333',
                }}
                cameraProps={{
                  initialPosition:{
                    latitude: .5,
                    longitude: .5,
                    distance: 2
                  }
                }}
              />
            </>}
          </Paper>
        </Grid>}
        {stlUrl && !queued && (!coeffs || Object.keys(coeffs).length === 0) && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Simulation Settings</Typography>
            <Grid container spacing={2}>
              {/*<Grid item xs={6}>
                <MultipleSelectCheckmarks label="Mach Number" value={mach || []} setValue={setMach} values={machs} />
              </Grid>*/}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Diameter</InputLabel>
                  <Select
                    value={diameter}
                    label="Diameter"
                    onChange={e => setDiameter(e.target.value)}
                  >
                    {calibers.map((caliber) => (
                      <MenuItem key={caliber} value={caliber}>{caliber}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box style={{ display: 'flex' }} justifyContent="center">
                  <Button variant="contained" onClick={queueCfd} disabled={diameter <= 0}>Start Simulation</Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>}
        {stlUrl && queued && (!coeffs || Object.keys(coeffs).length === 0) && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" style={{ textAlign: 'center' }}>
                  Queued for Simulation
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" style={{ textAlign: 'center' }} color="textSecondary">
                  This may take a several minutes, check back later.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>}
        {stlUrl && coeffs && Object.keys(coeffs).length > 0 && <>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2, mb: 2 }}>
                <Tabs onChange={(e, v) => setCoeffTab(v)} value={coeffTab} variant="scrollable" scrollButtons="auto">
                  {Object.keys(coeffs).map((key, i) => (
                    <Tab label={key} value={i} />
                  ))}
                </Tabs>
              </Box>
              {Object.keys(coeffs).filter((key, i) => i === coeffTab).map(key => (
                <Grid container spacing={2} key={key}>
                  <Grid item xs={12}>
                    <Typography variant="h4" style={{ textAlign: 'center' }}>
                      {key} Coefficients
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">Avg BC<sub>7</sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].avgBC, 3)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">BC<sub>7</sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].bc, 3)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">FF<sub>7</sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].ff, 3)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">C<sub>d<sub>0</sub></sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].cd, 3)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">C<sub>d<sub>α<sup>2</sup></sub></sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].cda2, 3)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">C<sub>m<sub>α</sub></sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].cma, 3)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">C<sub>L<sub>α</sub></sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].cla, 3)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={3} sx={{ padding: 2, textAlign: 'center' }}>
                      <Typography variant="h5" color="textSecondary">S<sub>g</sub></Typography>
                      <Typography variant="h6">{toFixed(coeffs[key].sg, 3)}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              ))}
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Drag Coefficients</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={dragCoeffChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="m" label={{ value: 'Mach' }} type="number" domain={[ Math.min(...aeroCoeffChartData.map(v => v.m)), Math.min(...aeroCoeffChartData.map(v => v.m)) ]} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="bc" stroke="#FF6384" strokeWidth={2} />
                  <Line type="monotone" dataKey="cd" stroke="#FF9F40" strokeWidth={2} />
                  <Line type="monotone" dataKey="ff" stroke="#9966FF" strokeWidth={2} />
                  <Line type="monotone" dataKey="g7Cd" stroke="#4BC0C0" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Aero Coefficients</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={aeroCoeffChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="m" label={{ value: 'Mach' }} type="number" domain={[ Math.min(...aeroCoeffChartData.map(v => v.m)), Math.min(...aeroCoeffChartData.map(v => v.m)) ]} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="cla" stroke="#FF6384" strokeWidth={2} />
                  <Line type="monotone" dataKey="cda2" stroke="#FF9F40" strokeWidth={2} />
                  <Line type="monotone" dataKey="cma" stroke="#9966FF" strokeWidth={2} />
                  <Line type="monotone" dataKey="sg" stroke="#4BC0C0" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex' }} >
                <Box sx={{ flexGrow: 1, p: 2, minWidth: '200px' }}>
                  <Tabs orientation="vertical" onChange={(e, v) => setRenderTab(v)} value={renderTab} variant="scrollable" scrollButtons="auto">
                    {Object.keys(renderings).map((key, i) => (
                      <Tab label={key} value={i} />
                    ))}
                  </Tabs>
                </Box>
                {Object.keys(renderings).filter((key, i) => i === renderTab).map(key => (
                  <Box key={key} sx={{ p: 2 }}>
                    <img src={renderings[key]} alt="rending" style={{ width: '100%' }} />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs onChange={(e, v) => setResTab(v)} value={resTab} variant="scrollable" scrollButtons="auto">
                  {Object.keys(chartData).map((key, i) => (
                    <Tab label={key} value={i} />
                  ))}
                </Tabs>
              </Box>
              {Object.keys(chartData).filter((key, i) => i === resTab).map(key => (
                <Box key={key}>
                  <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Convergence</Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData[key]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="times" label={{ value: 'Time', position: 'insideBottomLeft', offset: -10 }} />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="cd0" dot={false} stroke="#FF6384" strokeWidth={2} />
                      <Line type="monotone" dataKey="cd2" dot={false} stroke="#FF9F40" strokeWidth={2} />
                      <Line type="monotone" dataKey="cma2" dot={false} stroke="#9966FF" strokeWidth={2} />
                      <Line type="monotone" dataKey="cla2" dot={false} stroke="#4BC0C0" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ))}
            </Paper>
          </Grid>
        </>}
      </Grid>
    </Container>
  )
}

function MultipleSelectCheckmarks({ value, setValue, values, label }) {
  const handleChange = e => {
    const value = e.target.value
    const parsed = typeof value === 'string' ? value.split(',') : value
    parsed.sort((a, b) => a - b)
    setValue(parsed)
  }

  return (
    <div style={{ width: '100%' }}>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel id="demo-multiple-checkbox-label">{label}</InputLabel>
        <Select
          sx={{ width: '100%' }}
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          multiple
          value={value}
          onChange={handleChange}
          input={<OutlinedInput label={label} sx={{ width: '100%' }} />}
          renderValue={selected => (selected && selected.join) ? selected.join(', ') : ''}
          MenuProps={MenuProps}
        >
          {values.map((v) => (
            <MenuItem key={v} value={v}>
              <Checkbox checked={value.indexOf(v) > -1} />
              <ListItemText primary={v} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )
}

function getU (m, fahrenheit) {
  const speedOfSoundFPS = 49.02 * Math.sqrt(fahrenheit + 459.67);
  const speedOfSoundMPS = speedOfSoundFPS * 0.3048;
  let u = Math.round(speedOfSoundMPS * m)
  return Math.round(u)
}

export default function WrappedCfd () {
  return <AutoSaveProvider>
    <Cfd />
  </AutoSaveProvider>
}
